import { useState } from "react";
import {
  useInfiniteGetDefaultedLoansQuery,
  type GetDefaultedLoansQuery,
} from "~/types/graphql";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  getSortedRowModel,
  getPaginationRowModel,
} from "@tanstack/react-table";
import type { ColumnDef, SortingState } from "@tanstack/react-table";
import { Button } from "~/components/ui/button";

const formatUSD = (value: string) => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value));
};

const formatCollateral = (value: string) => {
  return `${Number(value).toFixed(4)} gOHM`;
};

const formatAddress = (address: string) => {
  return `${address.slice(0, 6)}...${address.slice(-4)}`;
};

const getEtherscanUrl = (address: string) => {
  return `https://etherscan.io/address/${address}`;
};

const columns: ColumnDef<GetDefaultedLoansQuery["coolerLoans"][number]>[] = [
  {
    accessorKey: "borrower",
    header: "Wallet Address",
    cell: ({ row }) => (
      <a
        href={getEtherscanUrl(row.original.borrower.id)}
        target="_blank"
        rel="noopener noreferrer"
        className="font-mono"
      >
        {formatAddress(row.original.borrower.id)}
      </a>
    ),
  },
  {
    accessorKey: "cooler",
    header: "Cooler",
    cell: ({ row }) => (
      <a
        href={getEtherscanUrl(row.original.cooler)}
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-500 hover:text-blue-700"
      >
        View Cooler
      </a>
    ),
  },
  {
    accessorKey: "currentExpiryTimestamp",
    header: "Expiry Date",
    cell: ({ row }) => {
      const date = new Date(Number(row.original.currentExpiryTimestamp) * 1000);
      return (
        <div>
          {date.toLocaleDateString()} {date.toLocaleTimeString()}
        </div>
      );
    },
    sortingFn: (a, b) => {
      return (
        a.original.currentExpiryTimestamp - b.original.currentExpiryTimestamp
      );
    },
  },
  {
    accessorKey: "principal",
    header: "Principal",
    cell: ({ row }) =>
      formatUSD(row.original.defaultedClaimEvents[0].defaultedPrincipal),
    sortingFn: (a, b) => {
      return (
        Number(a.original.defaultedClaimEvents[0].defaultedPrincipal) -
        Number(b.original.defaultedClaimEvents[0].defaultedPrincipal)
      );
    },
  },
  {
    accessorKey: "collateral",
    header: "Collateral",
    cell: ({ row }) =>
      formatCollateral(
        row.original.defaultedClaimEvents[0].collateralQuantityClaimed
      ),
    sortingFn: (a, b) => {
      return (
        Number(a.original.defaultedClaimEvents[0].collateralQuantityClaimed) -
        Number(b.original.defaultedClaimEvents[0].collateralQuantityClaimed)
      );
    },
  },
  {
    accessorKey: "collateralValue",
    header: "Collateral Value",
    cell: ({ row }) =>
      formatUSD(row.original.defaultedClaimEvents[0].collateralValueClaimed),
    sortingFn: (a, b) => {
      return (
        Number(a.original.defaultedClaimEvents[0].collateralValueClaimed) -
        Number(b.original.defaultedClaimEvents[0].collateralValueClaimed)
      );
    },
  },
  {
    accessorKey: "loanId",
    header: "Loan ID",
  },
];

export default function DefaultedLoans() {
  const { data, isLoading } = useInfiniteGetDefaultedLoansQuery(
    {
      first: 1000,
      skip: 0,
    },
    {
      getNextPageParam: (lastPage, allPages) => {
        if (lastPage.coolerLoans.length < 1000) {
          return undefined;
        }
        return { skip: allPages.length * 1000 };
      },
      initialPageParam: { skip: 0 },
    }
  );

  const defaultedLoans = data?.pages.flatMap((page) => page.coolerLoans) ?? [];

  //sum of all the principal values
  const totalPrincipal = defaultedLoans.reduce((acc, row) => acc + Number(row.defaultedClaimEvents[0].defaultedPrincipal), 0);

  //sum of all the collateral values
  const totalCollateral = defaultedLoans.reduce((acc, row) => acc + Number(row.defaultedClaimEvents[0].collateralValueClaimed), 0);
  
  //sum of all the collateral quantity values
  const totalCollateralQuantity = defaultedLoans.reduce((acc, row) => acc + Number(row.defaultedClaimEvents[0].collateralQuantityClaimed), 0);
  
  //sum of collateral minus total principal
  const totalCollateralMinusPrincipal = totalCollateral - totalPrincipal;

  console.log(totalPrincipal, totalCollateral, totalCollateralQuantity, totalCollateralMinusPrincipal, "the values");

  if (isLoading || !defaultedLoans) return <div>Loading...</div>;
  return <DataTable data={defaultedLoans} />;
}

const DataTable = ({ data }: { data: GetDefaultedLoansQuery["coolerLoans"] }) => {
  const [sorting, setSorting] = useState<SortingState>([
    { id: "currentExpiryTimestamp", desc: true },
  ]);

  const table = useReactTable({
    data: data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onSortingChange: setSorting,
    state: {
      sorting,
    },
    enableSorting: true,
    pageCount: Math.ceil(data.length / 25),
    initialState: {
      pagination: {
        pageSize: 25,
      },
    },
  });

  return (
    <div className="container mx-auto py-10">
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <h1 className="text-2xl font-bold">Defaulted Loans</h1>
          <span className="bg-blue-100 text-blue-800 text-sm font-medium px-2.5 py-0.5 rounded dark:bg-blue-900 dark:text-blue-300">
            Cooler V1
          </span>
        </div>
        <p className="text-muted-foreground">
          All loans that have been defaulted in Cooler V1
        </p>
      </div>
      <div className="bg-card rounded-md p-8 shadow">
        <div className="border rounded-md border-white/10">
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <TableHead
                      key={header.id}
                      className={
                        header.column.getCanSort()
                          ? "cursor-pointer select-none"
                          : ""
                      }
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                      {{
                        asc: " 🔼",
                        desc: " 🔽",
                      }[header.column.getIsSorted() as string] ?? null}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow key={row.id} className="border-b border-white/10">
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id} className="p-4">
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    No results.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
        <div className="flex items-center justify-between py-4">
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
            >
              Next
            </Button>
            <span className="flex items-center gap-1">
              <div>Page</div>
              <strong>
                {table.getState().pagination.pageIndex + 1} of{" "}
                {table.getPageCount()}
              </strong>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
